/**
 * PDF « Matières à rattraper » (2026-09-12) — accès léger de la scolarité.
 *
 * Délivré pour un étudiant redevable : identité, décision du semestre et
 * matières à rattraper par UE, SANS aucune note ni moyenne. Document sobre,
 * même charte que les statistiques de délibération.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { safeTxt } from '../Deliberation/statsDeliberationPdf';
import type { ReleveLegerAgent } from '../../services/relevesApi';

type RGB = [number, number, number];
const ENCRE: RGB = [30, 41, 59];
const GRIS: RGB = [100, 116, 139];
const FILET: RGB = [203, 213, 225];
const FOND_ENTETE: RGB = [241, 245, 249];

const DECALAGE_SEMESTRE: Record<string, number> = { L1: 0, L2: 2, L3: 4, M1: 0, M2: 2 };

async function chargerLogoBase64(src: string): Promise<string | null> {
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

export interface OptionsPdfRattrapage {
  leger: ReleveLegerAgent;
  institut?: { ville?: string; signataireTitre?: string; signataireNom?: string };
}

export async function genererPdfMatieresARattraper(o: OptionsPdfRattrapage): Promise<void> {
  const { leger } = o;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const MX = 18;
  const dateFr = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  const etu = leger.etudiant;
  const semAbsolu = (DECALAGE_SEMESTRE[(etu.niveau || '').toUpperCase()] ?? 0) + (leger.semestre || 0);

  const texte = (
    t: string, x: number, y: number,
    opt: { taille?: number; gras?: boolean; italique?: boolean; couleur?: RGB; align?: 'left' | 'center' | 'right' } = {},
  ) => {
    doc.setFont('helvetica', opt.gras ? 'bold' : (opt.italique ? 'italic' : 'normal'));
    doc.setFontSize(opt.taille ?? 9);
    doc.setTextColor(...(opt.couleur ?? ENCRE));
    doc.text(safeTxt(t), x, y, { align: opt.align ?? 'left' });
  };
  const filet = (y: number, epaisseur = 0.2, couleur: RGB = FILET) => {
    doc.setDrawColor(...couleur);
    doc.setLineWidth(epaisseur);
    doc.line(MX, y, W - MX, y);
  };

  // En-tête
  const logo = await chargerLogoBase64('/logo.png');
  if (logo) {
    try { doc.addImage(logo, 'PNG', MX, 10, 15, 15); } catch { /* logo facultatif */ }
  }
  const xId = logo ? MX + 19 : MX;
  texte('INSTITUT INTERNATIONAL 2i', xId, 16, { taille: 11, gras: true });
  texte('Instruire pour Mieux Servir', xId, 21, { taille: 8.5, italique: true, couleur: GRIS });
  if (leger.annee_academique) {
    texte(`Année académique ${leger.annee_academique}`, W - MX, 16, { taille: 8.5, couleur: GRIS, align: 'right' });
  }
  texte(`Édité le ${dateFr}`, W - MX, 21, { taille: 8.5, couleur: GRIS, align: 'right' });
  filet(28, 0.5, ENCRE);

  texte('MATIÈRES À RATTRAPER', W / 2, 40, { taille: 14, gras: true, align: 'center' });
  texte(`Semestre ${semAbsolu || leger.semestre}`, W / 2, 46, { taille: 9.5, couleur: GRIS, align: 'center' });

  // Identité
  autoTable(doc, {
    startY: 53,
    body: [
      ['Nom et prénoms', `${etu.nom || ''} ${etu.prenoms || ''}`.trim()],
      ['Matricule', etu.matricule || ''],
      ['Classe', etu.classe || ''],
      ['Option', etu.option || ''],
      ['Décision', leger.valide ? 'Semestre validé' : 'Semestre non validé'],
    ].map((r) => r.map(safeTxt)),
    theme: 'plain',
    margin: { left: MX, right: MX },
    styles: { font: 'helvetica', fontSize: 9, textColor: ENCRE, cellPadding: { top: 0.9, bottom: 0.9, left: 0, right: 2 } },
    columnStyles: { 0: { cellWidth: 34, textColor: GRIS }, 1: { fontStyle: 'bold' } },
  });
  let y = (doc as any).lastAutoTable.finalY + 7;

  // Matières par UE
  const lignes: string[][] = [];
  (leger.ues_a_rattraper || []).forEach((ue) => {
    const libUe = ue.prioritaire ? `${ue.nom_ue} (prioritaire)` : ue.nom_ue;
    if (ue.matieres.length === 0) {
      lignes.push([libUe, 'Aucune matière identifiée individuellement']);
    } else {
      ue.matieres.forEach((m, i) => lignes.push([i === 0 ? libUe : '', m.nom_matiere]));
    }
  });
  if (lignes.length === 0) lignes.push(['—', 'Aucune matière à rattraper']);

  autoTable(doc, {
    startY: y,
    head: [['Unité d\'enseignement', 'Matière à rattraper']],
    body: lignes.map((r) => r.map(safeTxt)),
    theme: 'grid',
    margin: { left: MX, right: MX },
    styles: { font: 'helvetica', fontSize: 9, textColor: ENCRE, lineColor: FILET, lineWidth: 0.15, cellPadding: 2 },
    headStyles: { fillColor: FOND_ENTETE, textColor: ENCRE, fontStyle: 'bold' },
    columnStyles: { 0: { cellWidth: 72, fontStyle: 'bold' } },
  });
  y = (doc as any).lastAutoTable.finalY + 6;

  const nbMatieres = (leger.ues_a_rattraper || []).reduce((n, ue) => n + ue.matieres.length, 0);
  const notes = [
    `${nbMatieres} matière${nbMatieres > 1 ? 's' : ''} à rattraper. Une UE « prioritaire » est éliminatoire : ses matières sont à rattraper en premier.`,
    'Document indicatif délivré sans les notes : le relevé complet sera disponible après régularisation de la situation financière.',
  ];
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(...GRIS);
  notes.forEach((n) => {
    const l = doc.splitTextToSize(safeTxt(n), W - 2 * MX);
    doc.text(l, MX, y);
    y += l.length * 3.8 + 1.5;
  });

  // Signature
  y += 8;
  texte(`Fait à ${o.institut?.ville || 'Pointe-Noire'}, le ${dateFr}`, W - MX, y, { taille: 9, align: 'right' });
  if (o.institut?.signataireTitre) {
    texte(o.institut.signataireTitre, W - MX, y + 6, { taille: 9, gras: true, align: 'right' });
  }
  if (o.institut?.signataireNom) {
    texte(o.institut.signataireNom, W - MX, y + 24, { taille: 9, align: 'right' });
  }

  doc.save(`Matieres_a_rattraper_${etu.matricule || 'etudiant'}_S${semAbsolu || leger.semestre}.pdf`);
}
